import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable, combineLatest, interval, map, of, startWith } from 'rxjs';

import { MaintenanceRepository } from '~shared/store/maintenance/maintenance.repository';
import { MaintenanceStatus } from '~shared/types/maintenance.types';

interface MaintenanceView {
	message: string;
	backAt: string | null;
	countdown: string | null;
}

@UntilDestroy()
@Component({
	selector: 'app-maintenance-page',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './maintenance-page.component.html',
	styleUrls: ['./maintenance-page.component.scss'],
})
export class MaintenancePageComponent implements OnInit {
	private static readonly DEFAULT_MESSAGE =
		'We voeren momenteel onderhoud uit aan de configurator. Bedankt voor je geduld.';

	public view$!: Observable<MaintenanceView>;

	constructor(
		private readonly maintenanceRepository: MaintenanceRepository,
		private readonly route: ActivatedRoute,
	) {}

	public ngOnInit(): void {
		// Preview mode (admin "voorbeeld" link): render the page from the draft
		// values passed as query params, independent of the live flag.
		const params = this.route.snapshot.queryParamMap;
		const status$: Observable<MaintenanceStatus> = params.has('preview')
			? of({
				enabled: true,
				message: params.get('msg'),
				endsAt: params.get('endsAt'),
			})
			: this.maintenanceRepository.status$;

		// Re-derive every second so the countdown ticks down live.
		this.view$ = combineLatest([
			status$,
			interval(1000).pipe(startWith(0)),
		]).pipe(
			map(([status]) => this.toView(status)),
			untilDestroyed(this),
		);
	}

	private toView(status: MaintenanceStatus): MaintenanceView {
		return {
			message: status.message?.trim() || MaintenancePageComponent.DEFAULT_MESSAGE,
			backAt: this.formatBackAt(status.endsAt),
			countdown: this.formatCountdown(status.endsAt),
		};
	}

	private formatBackAt(endsAt: string | null): string | null {
		const date = this.parseDate(endsAt);
		if (!date) {
			return null;
		}

		return new Intl.DateTimeFormat('nl-BE', {
			weekday: 'long',
			hour: '2-digit',
			minute: '2-digit',
		}).format(date);
	}

	private formatCountdown(endsAt: string | null): string | null {
		const date = this.parseDate(endsAt);
		if (!date) {
			return null;
		}

		const diffMs = date.getTime() - Date.now();
		if (diffMs <= 0) {
			return null;
		}

		const totalMinutes = Math.floor(diffMs / 60_000);
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;

		return hours > 0 ? `${hours} uur ${minutes} min` : `${minutes} min`;
	}

	private parseDate(value: string | null): Date | null {
		if (!value) {
			return null;
		}

		const date = new Date(value);
		return isNaN(date.getTime()) ? null : date;
	}
}
